import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ConfirmDialog from './ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TARGETS = [
  { value: 'all', label: 'Alle Kunden' },
  { value: 'active', label: 'Nur aktive Konten' },
  { value: 'with_investment', label: 'Kunden mit Investment' },
  { value: 'pending', label: 'Noch nicht aktiviert' },
];

const TEMPLATES = [
  {
    name: 'Neujahrsgruß',
    subject: 'Frohes neues Jahr von Value Plus',
    body: 'Liebe/r {{name}},\n\nwir wünschen Ihnen ein erfolgreiches neues Jahr und bedanken uns herzlich für Ihr Vertrauen.\n\nIhr Value Plus Team',
  },
  {
    name: 'Wartungsfenster',
    subject: 'Geplantes Wartungsfenster im Investor Portal',
    body: 'Liebe/r {{name}},\n\nam kommenden Sonntag von 02:00 – 04:00 Uhr führen wir geplante Wartungsarbeiten durch. Während dieser Zeit ist das Portal nicht erreichbar.\n\nVielen Dank für Ihr Verständnis.\n\nIhr Value Plus Team',
  },
  {
    name: 'Neue Funktion verfügbar',
    subject: 'Neue Funktion in Ihrem Investor Portal',
    body: 'Liebe/r {{name}},\n\nab heute steht Ihnen im Investor Portal eine neue Funktion zur Verfügung. Schauen Sie gleich rein!\n\nIhr Value Plus Team',
  },
];

const AdminBroadcast = () => {
  const [tab, setTab] = useState('compose');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [target, setTarget] = useState('active');
  const [recipientCount, setRecipientCount] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [viewing, setViewing] = useState(null);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/admin/broadcast/history`);
      setHistory(res.data || []);
    } catch {
      toast.error('Archiv konnte nicht geladen werden');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'archive') loadHistory();
  }, [tab]);

  // Live recipient count
  useEffect(() => {
    let cancel = false;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API}/admin/customers`);
        if (cancel) return;
        const all = res.data || [];
        let n = all.length;
        const st = (u) => (u.status || '').toLowerCase();
        if (target === 'active') n = all.filter((u) => st(u) === 'active').length;
        else if (target === 'pending') n = all.filter((u) => st(u) === 'pending').length;
        else if (target === 'with_investment') {
          // Frontend has no investment list per customer; use active users as a safe upper bound — backend filters exactly at send time.
          n = all.filter((u) => st(u) === 'active').length;
        }
        setRecipientCount(n);
      } catch {
        setRecipientCount(null);
      }
    };
    fetchCount();
    return () => { cancel = true; };
  }, [target]);

  const applyTemplate = (t) => {
    setSubject(t.subject);
    setBody(t.body);
  };

  const toHtml = (text) =>
    text
      .split('\n\n')
      .map((para) => `<p style="margin:0 0 12px 0;">${para.replace(/\n/g, '<br/>')}</p>`)
      .join('');

  const send = () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Betreff und Nachricht sind erforderlich');
      return;
    }
    setConfirmState({
      title: `An ${recipientCount ?? '?'} Empfänger senden?`,
      message: `Betreff: ${subject}\n\nDie E-Mail wird sofort versendet. Diese Aktion kann nicht rückgängig gemacht werden.`,
      confirmLabel: 'Jetzt senden',
      danger: false,
      onConfirm: async () => {
        setSending(true);
        try {
          const res = await axios.post(`${API}/admin/broadcast`, {
            subject: subject.trim(),
            message_html: toHtml(body.trim()),
            target,
          });
          toast.success(`${res.data.sent} von ${res.data.recipients} E-Mails versendet`);
          if (res.data.failed > 0) {
            toast.error(`${res.data.failed} fehlgeschlagen — siehe Audit-Log`);
          }
          setSubject('');
          setBody('');
          setTab('archive');
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Versand fehlgeschlagen');
        } finally {
          setSending(false);
        }
      },
    });
  };

  return (
    <div data-testid="admin-broadcast-page">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-heading font-bold">Bulk E-Mail</h2>
          <p className="text-sm text-muted-foreground mt-1">Sende eine E-Mail an mehrere Kunden gleichzeitig — mit Personalisierung und Audit-Log.</p>
        </div>
        <div className="inline-flex bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => setTab('compose')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition ${tab === 'compose' ? 'bg-white shadow-sm text-[#0A0A0A]' : 'text-muted-foreground'}`}
            data-testid="broadcast-tab-compose"
          >Neuer Versand</button>
          <button
            type="button"
            onClick={() => setTab('archive')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition ${tab === 'archive' ? 'bg-white shadow-sm text-[#0A0A0A]' : 'text-muted-foreground'}`}
            data-testid="broadcast-tab-archive"
          >Archiv</button>
        </div>
      </div>

      {tab === 'compose' && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card">
            <label className="block text-sm font-medium mb-1">Empfänger</label>
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="w-full px-3 py-2 border rounded mb-2"
              data-testid="broadcast-target"
            >
              {TARGETS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              {recipientCount !== null
                ? <>Empfänger: <strong className="text-accent">{recipientCount}</strong></>
                : 'Lade Empfänger …'}
            </p>
          </div>

          <div className="card">
            <label className="block text-sm font-medium mb-1">Betreff *</label>
            <input
              type="text"
              maxLength={200}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="z.B. Wichtige Information zu Ihrem Portfolio"
              className="w-full px-3 py-2 border rounded mb-4"
              data-testid="broadcast-subject"
            />

            <label className="block text-sm font-medium mb-1">Nachricht *</label>
            <textarea
              rows={12}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={'Liebe/r {{name}},\n\n…\n\nIhr Value Plus Team'}
              className="w-full px-3 py-2 border rounded resize-y font-sans text-sm leading-relaxed"
              data-testid="broadcast-body"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Platzhalter: <code className="bg-gray-100 px-1 rounded">{'{{name}}'}</code> wird durch den Namen jedes Kunden ersetzt.
              Leerzeilen erzeugen Absätze.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => { setSubject(''); setBody(''); }}
              disabled={sending}
              className="btn-secondary"
            >
              Zurücksetzen
            </button>
            <button
              type="button"
              onClick={send}
              disabled={sending || !subject.trim() || !body.trim()}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="broadcast-send-button"
            >
              {sending ? 'Versendet …' : `An ${recipientCount ?? '…'} senden`}
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="card">
            <h3 className="font-heading font-semibold mb-3">Vorlagen</h3>
            <div className="space-y-2">
              {TEMPLATES.map((t) => (
                <button
                  key={t.name}
                  type="button"
                  onClick={() => applyTemplate(t)}
                  className="w-full text-left px-3 py-2 rounded border hover:border-accent hover:bg-accent/5 text-sm transition"
                  data-testid={`broadcast-template-${t.name}`}
                >
                  <p className="font-medium">{t.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{t.subject}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="card bg-amber-50 border-amber-200">
            <p className="text-xs text-amber-900 leading-relaxed">
              <strong>Wichtig:</strong> Versand sofort. E-Mails landen direkt im Postfach der Kunden. Jeder Versand wird im Audit-Log protokolliert (Betreff, Empfängerzahl, Erfolg/Fehler).
            </p>
          </div>
        </div>
      </div>
      )}

      {tab === 'archive' && (
      <div className="card" data-testid="broadcast-archive">
        {historyLoading ? (
          <p className="text-sm text-muted-foreground text-center py-8">Lade Archiv …</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">Noch keine Broadcasts versendet.</p>
        ) : (
          <ul className="divide-y">
            {history.map((h) => {
              const rate = h.recipient_count > 0 ? Math.round((h.sent / h.recipient_count) * 100) : 0;
              return (
                <li key={h.id} className="py-3 flex items-center gap-3" data-testid={`broadcast-history-${h.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{h.subject || '(Kein Betreff)'}</p>
                    <p className="text-xs text-muted-foreground tabular-nums">
                      {h.created_at ? new Date(h.created_at).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                      {h.actor_email ? ` · ${h.actor_email}` : ''}
                      {h.target ? ` · ${h.target}` : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium tabular-nums">
                      {h.sent}/{h.recipient_count}
                      <span className={`ml-2 inline-flex px-2 py-0.5 rounded-full text-[10px] ${rate >= 95 ? 'bg-green-100 text-green-800' : rate >= 70 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-700'}`}>
                        {rate}%
                      </span>
                    </p>
                    {h.failed > 0 && <p className="text-[11px] text-red-600 tabular-nums">{h.failed} fehlgeschlagen</p>}
                  </div>
                  <button
                    onClick={() => setViewing(h)}
                    className="text-xs px-3 py-1.5 bg-secondary hover:bg-gray-200 rounded transition"
                    data-testid={`broadcast-view-${h.id}`}
                  >Ansehen</button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      )}

      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 vp-anim-fade" onClick={() => setViewing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto vp-anim-scale" data-testid="broadcast-view-modal">
            <div className="p-6 border-b">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Versendet am {viewing.created_at ? new Date(viewing.created_at).toLocaleString('de-DE') : '—'} · {viewing.actor_email || ''}</p>
                  <h3 className="font-heading text-lg font-bold mt-1">{viewing.subject}</h3>
                  <p className="text-xs text-muted-foreground mt-1">Ziel: {viewing.target} · {viewing.sent}/{viewing.recipient_count} zugestellt{viewing.failed ? ` · ${viewing.failed} Fehler` : ''}</p>
                </div>
                <button onClick={() => setViewing(null)} className="text-muted-foreground hover:text-black">✕</button>
              </div>
            </div>
            <div className="p-6">
              {viewing.message_html ? (
                <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: viewing.message_html }} />
              ) : (
                <p className="text-sm text-muted-foreground italic">Kein Nachrichteninhalt im Archiv (älterer Eintrag).</p>
              )}
            </div>
            <div className="px-6 py-4 border-t flex justify-between gap-2">
              <button
                type="button"
                onClick={() => {
                  setSubject(viewing.subject || '');
                  // strip <p>/<br/> to plain
                  const plain = (viewing.message_html || '')
                    .replace(/<p[^>]*>/g, '')
                    .replace(/<\/p>/g, '\n\n')
                    .replace(/<br\s*\/?>/g, '\n')
                    .replace(/<[^>]+>/g, '')
                    .trim();
                  setBody(plain);
                  setViewing(null);
                  setTab('compose');
                  toast.success('Als neuen Entwurf geladen');
                }}
                className="btn-secondary text-sm"
                data-testid="broadcast-reuse-button"
              >Erneut verwenden</button>
              <button onClick={() => setViewing(null)} className="btn-primary text-sm">Schließen</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
};

export default AdminBroadcast;
